import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Development-only guard: after every route change, verify the page put a
 * <link rel="canonical"> (or an explicit noindex) into <head>. If Helmet ever
 * stops reaching <head> again — provider removed, duplicate instance, missing
 * per-page tags — this shouts in the console instead of failing silently.
 *
 * Renders nothing and is compiled out of production builds.
 */
const CanonicalGuard = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    // Give the route's Helmet a beat to commit its head mutations.
    const t = window.setTimeout(() => {
      const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      const robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
      const noindex = (robots?.content ?? '').includes('noindex');
      if (!canonical && !noindex) {
        console.error(
          `[LED Logic] Route "${pathname}" rendered without <link rel="canonical"> and without a noindex meta. ` +
            'Add a <Helmet> block with a title, description and canonical (https://www.ledlogic.tech<path>).'
        );
        return;
      }
      if (canonical) {
        const href = canonical.href;
        if (!href.startsWith('https://www.ledlogic.tech')) {
          console.error(`[LED Logic] Route "${pathname}" canonical is not on the canonical host: ${href}`);
        }
        if (href.includes('?') || href.includes('#')) {
          console.error(`[LED Logic] Route "${pathname}" canonical must not include a query string or hash: ${href}`);
        }
      }
      if (!document.title || document.title.trim() === '') {
        console.error(`[LED Logic] Route "${pathname}" rendered without a <title>.`);
      }
    }, 400);
    return () => window.clearTimeout(t);
  }, [pathname]);

  return null;
};

export default CanonicalGuard;
